import {
  Box,
  Tabs,
  Tab,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  useTheme
} from "@mui/material";

import {
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  ReportOutlined as ReportOutlinedIcon
} from "@mui/icons-material";

import t from "../../helpers/en";
import { USER_TYPE_ACCOUNT } from "../../helpers/constants";

import Search from "../../components/Inputs/SearchInput";
import CheckboxGroup from "../../components/Inputs/CheckboxGroup";
import Radio from "../../components/Inputs/RadioGroup";

import { styleIconBtn, freeTierStyles } from "../../GeneralStyles";

const getGroupStyles = (isDarkMode) => ({
  display: "grid",
  gap: "1rem",
  alignItems: "center",
  borderBottom: isDarkMode ? "1px solid rgba(255,255,255,.2)" : "1px solid rgba(0,0,0,.2)",
  gridTemplateColumns: "1fr 1fr 1fr minmax(2em,5em) repeat(2, 2em)",
  padding: "0.5rem 0.2rem",
});

const getUserStyles = (isDarkMode) => ({
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "1fr 5em 2em",
  borderBottom: isDarkMode ? "1px solid rgba(255,255,255,.2)" : "1px solid rgba(0,0,0,.2)",
  padding: "0.5rem 0.2rem",
});

const ManageProjects = (props) => {
  const {
    data,
    onChangeTab,
    onChangeSearchTxt,
    onClearSearchTxt,
    onKeyPressSearch,
    onRemoveUser,
    onKeyPressSearchUser,
    onChangeCheckbox,
    onClearSearchTxtUser,
    onChangeSearchTxtUser,
    onChangeTxt,
    onDeleteGroup,
    onEditGroup,
    onSetGroup,
    modalData,
    currentGroup,
    currentUser,
    onToggleLockedStatus,
  } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const groupStyles = getGroupStyles(isDarkMode);
  const userStyles = getUserStyles(isDarkMode);

  const textColor = isDarkMode ? "#e2e8f0" : "#1e293b";
  const mutedColor = isDarkMode ? "#94a3b8" : "#64748b";

  return (
    <Box>
      <Tabs
        value={modalData?.tabValue}
        onChange={onChangeTab}
        aria-label="select a group"
        sx={{ mb: 2 }}
      >
        {[
          { id: 0, label: t.selectProject },
          {
            id: 1,
            label: !modalData?.id ? (
              currentUser?.account?.billingEnabled ? (
                t.newProject
              ) : (
                <span style={{ display: "flex", alignItems: "center" }}>
                  <LockIcon style={{ marginRight: 5, color: "gray" }} />
                  {t.newProject}
                </span>
              )
            ) : (
              t.edit(t.project)
            ),
            disabled: !modalData?.id && !currentUser?.account?.billingEnabled,
          },
        ].map((tab, tabKey) => (
          <Tab
            key={tabKey}
            label={tab.label}
            id={`group-tab-${tab.id}`}
            aria-controls={`group-tabpanel-${tab.id}`}
            disabled={tab?.disabled}
          />
        ))}
      </Tabs>

      {/* free tier */}
      {!currentUser?.account?.billingEnabled && (
        <sub style={freeTierStyles}>
          <InfoIcon /> {t.freeTier}
        </sub>
      )}

      {modalData?.tabValue === 0 ? (
        <>
          <Search
            className={{
              marginLeft: "auto",
              width: "100%",
              marginBottom: "1rem",
            }}
            searchedValue={modalData?.searchedValue}
            {...{
              onChangeSearchTxt,
              onClearSearchTxt,
              onKeyPressSearch,
            }}
            specificDetails={{
              styles: {
                searchIcon: { width: "1.5em", height: "auto" },
              },
            }}
          />
          {data.all?.length > 0 ? (
            <>
              <span style={{ ...groupStyles, color: textColor }}>
                {[t.name, t.id, t.userRights,
                      <span key="locked-header">
                        <span>{t.locked}</span>
                        <Tooltip title={t.lockedLabel} arrow>
                          <ReportOutlinedIcon sx={{ fill: "#ff9800 !important", verticalAlign: "top", marginLeft: "2px" }} fontSize="small" />
                        </Tooltip>
                      </span>].map((it, idx) => (
                  <span style={{ fontWeight: 600, color: textColor }} key={idx}>
                    {it}
                  </span>
                ))}
              </span>
              {data?.filtered?.map((it) => {
                let updatedGroup = null;

                if (
                  currentUser?.userType === USER_TYPE_ACCOUNT ||
                  currentGroup?.id !== it?.id
                ) {
                  updatedGroup = it;
                }

                return (
                  <span key={it.id} style={{ ...groupStyles, color: textColor }}>
                    <span
                      onClick={() => onSetGroup(updatedGroup)}
                      style={{
                        cursor: "pointer",
                        alignContent: "center",
                        width: "100%",
                        color: textColor,
                      }}
                    >
                      <Radio
                        onChange={() => onSetGroup(updatedGroup)}
                        specificDetails={{
                          isIndividual: true,
                          checked: it.id === currentGroup?.id,
                          value: it.id,
                        }}
                      />
                      {it.name}
                    </span>
                    <span style={{ color: mutedColor, fontSize: "0.85rem" }}>{it.id}</span>
                    <span style={{ color: textColor }}>
                      {it?.isContributor2Group ? t.contributor : t.readOnly}
                    </span>
                    <CheckboxGroup
                      specificDetails={{
                        isSingleInput: true,
                        checked: it.locked,
                        formControlLabelStyle: { margin: 0 },
                        checkboxStyle: {
                          padding: 0,
                          "& .MuiSvgIcon-root": {
                            width: "1.5em",
                            height: "auto",
                          },
                        },
                      }}
                      onChange={() => onToggleLockedStatus(it)}
                    />
                    <IconButton
                      sx={{
                        ...styleIconBtn,
                        width: "1.5em",
                        height: "1.5em",
                      }}
                      title={t.edit(t.appProject)}
                      onClick={(evt) => onEditGroup(evt, it)}
                      disabled={!it?.isContributor2Group}
                    >
                      <EditIcon sx={{ pointerEvents: "none" }} />
                    </IconButton>
                    <IconButton
                      sx={{
                        ...styleIconBtn,
                        width: "1.5em",
                        height: "1.5em",
                      }}
                      title={t.delete(t.appProject)}
                      onClick={() => onDeleteGroup(it)}
                      disabled={!it?.isContributor2Group || (!currentUser?.account?.billingEnabled)}
                    >
                      <DeleteIcon sx={{ pointerEvents: "none" }} />
                    </IconButton>
                  </span>
                );
              })}
            </>
          ) : (
            <span
              style={{
                display: "flex",
                marginTop: "1rem",
                justifyContent: "center",
                color: mutedColor,
              }}
            >
              {t.noProject}
            </span>
          )}
        </>
      ) : (
        <span style={{ display: "grid", gap: "1rem" }}>
          <TextField
            id={`outlined-basic-name`}
            label={t.projectN}
            name="name"
            value={modalData?.name || ""}
            onChange={onChangeTxt}
            size="small"
            required
            error={
              // SCK-721: project name is required. Show error state only
              // after the user has interacted — don't flag a pristine
              // empty field on first render. `touchedName` is set by the
              // onChangeTxt handler the first time the user types.
              Boolean(modalData?.touchedName) &&
              (modalData?.name == null || modalData.name.trim() === "")
            }
            helperText={
              Boolean(modalData?.touchedName) &&
              (modalData?.name == null || modalData.name.trim() === "")
                ? "Project name is required."
                : " "
            }
          />
          <Search
            searchedValue={modalData?.searchedUser}
            onChangeSearchTxt={onChangeSearchTxtUser}
            onClearSearchTxt={onClearSearchTxtUser}
            onKeyPressSearch={onKeyPressSearchUser}
            specificDetails={{
              endIc: modalData?.isSearchingUser && <CircularProgress />,
              placeholder: "Search User by Email",
              styles: {
                searchIcon: { width: "1.5em", height: "auto" },
                paper: {
                  width: "100%",
                  marginBottom: "1rem",
                  boxShadow: "none",
                  border: isDarkMode ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.3)",
                },
              },
            }}
          />
          {modalData?.selectedUsers?.length > 0 && (
            <>
              <span style={{ ...userStyles, color: textColor }}>
                {[t.users, "Contributor"].map((it, itIdx) => (
                  <span style={{ fontWeight: 600, color: textColor }} key={itIdx}>
                    {it}
                  </span>
                ))}
              </span>
              <span style={{ display: "grid", gap: "1rem" }}>
                {modalData?.selectedUsers?.map((it, idx) => (
                  <span key={it.id} style={{ ...userStyles, color: textColor }}>
                    <span style={{ color: textColor }}>{idx + 1}. {it.email}</span>
                    <CheckboxGroup
                      specificDetails={{
                        isSingleInput: true,
                        checked: Boolean(it.isContributor),
                        name: "isContributor",
                        disabled: it?.id === currentUser?.uid,
                        formControlLabelStyle: { margin: 0 },
                        checkboxStyle: {
                          padding: 0,
                          "& .MuiSvgIcon-root": {
                            width: "1.5em",
                            height: "auto",
                          },
                        },
                      }}
                      onChange={(e) => onChangeCheckbox(e, it, idx)}
                    />
                    <IconButton
                      onClick={() => {
                        onRemoveUser(idx);
                      }}
                      sx={{
                        ...styleIconBtn,
                        width: "1.5em",
                        height: "1.5em",
                      }}
                      disabled={it?.id === modalData?.currentLoggedInUser?.id}
                    >
                      <RemoveIcon sx={{ pointerEvents: "none" }} />
                    </IconButton>
                  </span>
                ))}
              </span>
            </>
          )}
        </span>
      )}
    </Box>
  );
};

export default ManageProjects;
